import { TrainingResourceBuilderActionInput } from "@src/types.generated";
import { useCallback, useContext } from "react";
import {
  BuilderActionContext,
  BuilderActionPartial,
} from "@contexts/BuilderActionContext";
import { useAtomCallback } from "jotai/utils";
import { trainingResourceIdAtom } from "@src/components/libraryItemDetailPages/resource/atoms";

type RichTextDocActionPayload = {
  action: TrainingResourceBuilderActionPartial;
  inverseAction: TrainingResourceBuilderActionPartial | null;
};
type TrainingResourceBuilderActionPartial = {
  payload: Omit<TrainingResourceBuilderActionInput, "trainingResourceId">;
};

type DispatchTrainingResourceBuilderActionArgs = (
  payload: RichTextDocActionPayload | RichTextDocActionPayload[],
  skipAddToUndoStack?: boolean,
) => void;

export const useDispatchTrainingResourceBuilderAction =
  (): DispatchTrainingResourceBuilderActionArgs => {
    const builderActionContext = useContext(BuilderActionContext);
    return useAtomCallback(
      useCallback(
        (get, _set, x, skipAddToUndoStack) => {
          const trainingResourceId = get(trainingResourceIdAtom);
          if (!trainingResourceId || !builderActionContext) return;
          const itemList = x instanceof Array ? x : [x];
          builderActionContext.dispatchAction(
            { trainingResourceId },
            itemList.map((item) => ({
              action: convertToBuilderActionPartial(
                item.action,
                trainingResourceId,
              ),
              inverseAction: item.inverseAction
                ? convertToBuilderActionPartial(
                    item.inverseAction,
                    trainingResourceId,
                  )
                : null,
            })),
            !!skipAddToUndoStack,
          );
        },
        [builderActionContext],
      ),
    );
  };

const convertToBuilderActionPartial = (
  trainingResourceAction: TrainingResourceBuilderActionPartial,
  trainingResourceId: string,
): BuilderActionPartial => {
  return {
    ...trainingResourceAction,
    timestamp: new Date(),
    payload: {
      trainingResourceBuilderAction: {
        trainingResourceId,
        ...trainingResourceAction.payload,
      },
    },
  };
};
